import { readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import type { NameMatch } from "./lookup.ts";
import type { ChannelState, ChatLine } from "./store.ts";

/**
 * The chat panel's scope tabs as plain data (P1.9-T41, ADR-068's amendment "scope tabs, and Help's icon"): All, then
 * a tab for each scope the page speaks, in the design's order less the reserved scopes, Help after Trade. Each tab
 * filters the log; a scope's tab counts the lines of its scope that arrived from other players while another scope
 * tab was chosen; the chosen tab is kept per browser; and a draft that is no command speaks to the chosen tab's
 * scope. Pure: the panel holds the state and hands it in.
 */

/**
 * Every tab, in the row's order.
 */
export const CHAT_TABS = ["all", "local", "global", "trade", "help", "party", "whisper"] as const;

/**
 * A tab.
 */
export type ChatTab = (typeof CHAT_TABS)[number];

/**
 * A tab of one scope: every tab but All.
 */
export type ScopeTab = Exclude<ChatTab, "all">;

/**
 * The scope tabs, in the row's order.
 */
export const SCOPE_TABS: readonly ScopeTab[] = ["local", "global", "trade", "help", "party", "whisper"];

/**
 * The newest line id each scope's tab has shown.
 */
export type TabSeen = Readonly<Record<ScopeTab, number>>;

/**
 * How many lines each scope's tab has not shown.
 */
export type TabCounts = Readonly<Record<ScopeTab, number>>;

/**
 * No scope's tab has shown a line: where the page starts.
 */
export const NOTHING_SEEN: TabSeen = { local: 0, global: 0, trade: 0, help: 0, party: 0, whisper: 0 };

/**
 * The storage key the chosen tab is kept under.
 */
export const CHAT_TAB_KEY = "odyssey.chat.tab";

/**
 * The most a badge counts before it reads "9+".
 */
const BADGE_MOST = 9;

/**
 * The command each tab of a scope that is no Local turns a draft into: the channels' commands, Party's, and a reply for
 * Whisper.
 */
const TAB_COMMANDS: Readonly<Partial<Record<ChatTab, string>>> = { global: "/g", trade: "/t", help: "/h", party: "/p", whisper: "/re" };

/**
 * The tabs open for the channels the gateway sent: All, Local and Whisper with the chat, Global, Trade and Help once
 * the gateway has sent the channels, whether or not the character is in them, and Party only while the character is
 * seated in a party (ADR-101 decision 25).
 *
 * @param channels The channels as the gateway last sent them, or null before it has.
 * @param party Whether the character is in a party.
 * @returns The open tabs, in the row's order.
 */
export function openTabs(channels: ChannelState | null, party = false): readonly ChatTab[] {
  return CHAT_TABS.filter((tab) => (tab === "party" ? party : channels !== null || tab === "all" || tab === "local" || tab === "whisper"));
}

/**
 * The scope a line belongs to among the tabs, or what it is if none: `every` for a line every tab shows (the page's
 * information, an error line and `/ping`'s panel, the answers to what the player typed, wherever it was typed), `all`
 * for one only All shows (a death line).
 *
 * @param line The line.
 * @returns Its scope's tab, `every` or `all`.
 */
function lineScope(line: ChatLine): ScopeTab | "every" | "all" {
  switch (line.kind) {
    case "local":
    case "action":
      return "local";
    case "whisper":
      return "whisper";
    case "channel":
      return line.channel;
    case "party":
      return "party";
    case "system":
    case "error":
    case "ping":
      return "every";
    case "death":
      return "all";
  }
}

/**
 * Whether a tab shows a line.
 *
 * @param tab The tab.
 * @param line The line.
 * @returns True when All, when the line is the tab's scope's, or when it is the page's information, an error line or
 *   `/ping`'s panel.
 */
export function showsLine(tab: ChatTab, line: ChatLine): boolean {
  const scope = lineScope(line);
  return tab === "all" || scope === "every" || scope === tab;
}

/**
 * The tab the panel shows: the kept one while it is open, else All, the kept choice waiting for its tab.
 *
 * @param kept The kept choice.
 * @param open The open tabs.
 * @returns The tab shown.
 */
export function shownTab(kept: ChatTab, open: readonly ChatTab[]): ChatTab {
  return open.includes(kept) ? kept : "all";
}

/**
 * Reads a kept value as a tab.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns The tab it names exactly, or All for anything else.
 */
export function parseChatTab(raw: string | null): ChatTab {
  return CHAT_TABS.find((tab) => tab === raw) ?? "all";
}

/**
 * Reads the kept tab.
 *
 * @param store Where it is kept, or null when there is none.
 * @returns The tab, All when nothing usable is kept or the store cannot be read.
 */
export function readChatTab(store: SettingsStore | null): ChatTab {
  return readSetting(CHAT_TAB_KEY, parseChatTab, store);
}

/**
 * Keeps the chosen tab.
 *
 * @param tab The tab.
 * @param store Where to keep it, or null when there is none.
 * @returns True when the store took it.
 */
export function writeChatTab(tab: ChatTab, store: SettingsStore | null): boolean {
  return writeSetting(CHAT_TAB_KEY, tab, store);
}

/**
 * What each scope's tab has seen once the shown tab has shown the log: under All every scope has seen the newest line,
 * and under a scope's tab that scope has. Line ids only grow, so the newest line's id stands for every line before it,
 * through `/clear` and the log's cap alike.
 *
 * @param seen What each had seen.
 * @param shown The tab shown.
 * @param lines The log.
 * @returns The new record, or `seen` itself when nothing changed, so a caller can keep its state.
 */
export function seeTabs(seen: TabSeen, shown: ChatTab, lines: readonly ChatLine[]): TabSeen {
  const newest = lines.at(-1)?.id;
  if (newest === undefined) {
    return seen;
  }
  const scopes = shown === "all" ? SCOPE_TABS : [shown];
  if (scopes.every((scope) => seen[scope] >= newest)) {
    return seen;
  }
  const next: Record<ScopeTab, number> = { ...seen };
  for (const scope of scopes) {
    next[scope] = Math.max(next[scope], newest);
  }
  return next;
}

/**
 * Whether a line was written by the player: a whisper sent, or a line whose speaker has the player's names.
 *
 * @param line The line.
 * @param self The player's names, or null before the welcome.
 * @returns True for the player's own line.
 */
function ownLine(line: ChatLine, self: NameMatch | null): boolean {
  if (line.kind === "whisper") {
    return line.sent;
  }
  if (self === null || !("firstName" in line)) {
    return false;
  }
  return line.firstName.toLowerCase() === self.firstName.toLowerCase() && line.familyName.toLowerCase() === self.familyName.toLowerCase();
}

/**
 * How many lines each scope's tab has not shown: its scope's lines from other players newer than the newest it has
 * seen.
 *
 * @param lines The log.
 * @param seen What each scope's tab has seen.
 * @param self The player's names, or null before the welcome.
 * @returns The counts.
 */
export function unreadCounts(lines: readonly ChatLine[], seen: TabSeen, self: NameMatch | null): TabCounts {
  const counts: Record<ScopeTab, number> = { ...NOTHING_SEEN };
  for (const line of lines) {
    const scope = lineScope(line);
    if (scope !== "every" && scope !== "all" && line.id > seen[scope] && !ownLine(line, self)) {
      counts[scope] += 1;
    }
  }
  return counts;
}

/**
 * A badge's text.
 *
 * @param count The lines not shown.
 * @returns The count up to 9, "9+" past it, or null for none, which shows no badge.
 */
export function badgeText(count: number): string | null {
  if (count <= 0) {
    return null;
  }
  return count > BADGE_MOST ? `${BADGE_MOST}+` : String(count);
}

/**
 * The draft as it is sent under a tab: a draft that is no command speaks to the chosen tab's scope, as `/g`, `/t` or
 * `/h` under a channel's tab and as a reply under Whisper's; a command, a blank draft, and any draft under All or
 * Local are sent as typed.
 *
 * @param draft The input's text.
 * @param tab The tab shown.
 * @returns The draft to send and to name the scope of.
 * @example
 * tabDraft("wts sword", "trade");
 * // "/t wts sword"
 */
export function tabDraft(draft: string, tab: ChatTab): string {
  const command = TAB_COMMANDS[tab];
  if (command === undefined || draft.trim() === "" || draft.trimStart().startsWith("/")) {
    return draft;
  }
  return `${command} ${draft}`;
}

/**
 * The draft the entry row names the scope of under a tab: a blank draft as the tab's scope's command alone, so the
 * row says where a line typed there will go before a letter is typed, and any other draft as `tabDraft` sends it.
 *
 * @param draft The input's text.
 * @param tab The tab shown.
 * @returns The draft to name the scope of.
 */
export function labelDraft(draft: string, tab: ChatTab): string {
  const command = TAB_COMMANDS[tab];
  return draft.trim() === "" ? (command === undefined ? "" : `${command} `) : tabDraft(draft, tab);
}

/**
 * Whom a draft under a tab replies to: under Whisper a draft that is no command is sent as `/re`, which reaches the
 * newest player who whispered as the line is sent, so the entry row names that player beforehand, or says there is
 * nobody to reply to.
 *
 * @param tab The tab shown.
 * @param draft The input's text.
 * @param whisperers The recent whisperers, newest first, as a reply reads them.
 * @returns The player the line will reach, nobody, or null where the draft is no reply under a tab.
 */
export function replyTarget(tab: ChatTab, draft: string, whisperers: readonly NameMatch[]): { readonly kind: "to"; readonly name: NameMatch } | { readonly kind: "nobody" } | null {
  if (tab !== "whisper" || draft.trimStart().startsWith("/")) {
    return null;
  }
  const newest = whisperers[0];
  return newest === undefined ? { kind: "nobody" } : { kind: "to", name: newest };
}

/**
 * Where a row that scrolls sideways must stand to show one of its items whole, moving no further than it must: where
 * it stands for an item in view, the item's left edge for one cut on the left or wider than the view, and the
 * item's right edge at the view's right for one cut on the right.
 *
 * @param scrollLeft Where the row stands.
 * @param view The row's visible width.
 * @param left The item's left edge from the row's start.
 * @param width The item's width.
 * @returns Where the row should stand, in the same units.
 * @example
 * scrollToShow(0, 126, 150, 39);
 * // 63
 */
export function scrollToShow(scrollLeft: number, view: number, left: number, width: number): number {
  if (width >= view || left < scrollLeft) {
    return left;
  }
  return left + width > scrollLeft + view ? left + width - view : scrollLeft;
}

/**
 * The scope whose unread count just rose, so a narrow panel's tab row can bring its tab and badge into view: the
 * last in the row's order when several rose at once.
 *
 * @param before The counts before.
 * @param after The counts now.
 * @returns The scope, or null when no count rose.
 */
export function newlyUnread(before: TabCounts, after: TabCounts): ScopeTab | null {
  return [...SCOPE_TABS].reverse().find((scope) => after[scope] > before[scope]) ?? null;
}
